import axios from "axios";
import { jwtDecode } from "jwt-decode";

import { env } from "config/env";
import i18n from "multitenancy/i18n";
import { getStore } from "store";

export const getAcceptLanguage = () => {
  const state = getStore().getState();
  const globalConfigurations = state.configurations?.globalConfigurations;
  const appLangConfig = Array.isArray(globalConfigurations)
    ? globalConfigurations.find((c: any) => c.name === "ApplicationLanguage")
    : undefined;

  if (appLangConfig?.value) return appLangConfig.value;

  return i18n.language || env.DEFAULT_LANGUAGE || "pt-BR";
};

import { logout, refreshSuccess } from "store/modules/auth/actions";
import { hideLoading } from "store/modules/loading/actions";
export const baseURL = import.meta.env.VITE_APP_BASEURL;
export const storageBaseUrl = import.meta.env.VITE_APP_STORAGE_BASE_URL;
export const baseBlob = import.meta.env.VITE_APP_STORAGE_BASE_URL;

const api = axios.create({
  baseURL,
});

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

let refreshPromise: Promise<{
  new_refresh_Token: string;
  new_access_Token: string;
}> | null = null;

const refreshToken = async (
  {
    access_Token,
    refresh_Token,
  }: { access_Token: string; refresh_Token: string },
  retries = 3,
): Promise<{
  new_refresh_Token: string;
  new_access_Token: string;
}> => {
  try {
    const { data } = await axios.post(
      baseURL + "/v1/token/refresh",
      {
        access_Token,
        refresh_Token,
        platform: 0,
      },
      {
        headers: { "Accept-Language": getAcceptLanguage() },
      },
    );

    return {
      new_refresh_Token: data.data.refresh_Token as string,
      new_access_Token: data.data.access_Token as string,
    };
  } catch {
    if (retries > 0) {
      await delay(3000);
      return refreshToken({ access_Token, refresh_Token }, retries - 1);
    } else {
      getStore().dispatch(logout());
      getStore().dispatch(hideLoading());
      throw new Error("Failed to refresh token after multiple attempts");
    }
  }
};

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? 0;

      if (status === 404) {
        return Promise.resolve(error.response);
      }
    }

    return Promise.reject(error);
  },
);

api.interceptors.request.use(async (config: any) => {
  const dNow = new Date();

  const state = getStore().getState();

  const token = state.auth.accessToken;
  const refresh_Token = state.auth.refreshToken;
  const expireIn = state.auth.expireIn;
  let shouldRefresh = false;

  const expired = expireIn ? dNow >= new Date(expireIn) : false;

  if (token && refresh_Token && expired) {
    shouldRefresh = true;
  }

  const acceptLanguage = getAcceptLanguage();

  if (shouldRefresh) {
    try {
      if (!refreshPromise) {
        refreshPromise = refreshToken({
          access_Token: token!,
          refresh_Token: refresh_Token!,
        });
      }

      const { new_refresh_Token, new_access_Token } = await refreshPromise;

      refreshPromise = null;

      const decodedToken = jwtDecode<{ exp: number }>(new_access_Token);
      const expireIn = new Date((decodedToken.exp - 300) * 1000).toISOString();

      getStore().dispatch(
        refreshSuccess({
          accessToken: new_access_Token,
          refreshToken: new_refresh_Token,
          expireIn,
        }),
      );

      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${new_access_Token}`,
        "Accept-Language": acceptLanguage,
      };
    } catch {
      config.headers = { Authorization: "", "Accept-Language": acceptLanguage };
    }

    return config;
  } else if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
      "Accept-Language": acceptLanguage,
    };
  }

  return config;
});

export default api;
